import { useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import type { PartyContactDto, PartyContactRequest, PartyType } from "@/types/parties";
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label, StatusBadge } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { useDialogs } from "@/hooks/useDialogs";
import { displayEmail } from "@/design-system/lib/format";
import { useDeletePartyContact, useUpsertPartyContact } from "@/hooks/parties/useParties";

// Party read-flip (WS-A) — inline contacts editor for the party Overview
// tab. Contacts are info-only (never a login): a small inline form covers
// add + edit; primary is a single holder enforced server-side.

interface ContactFormState {
  id?: string;
  firstName: string;
  lastName: string;
  title: string;
  email: string;
  phone: string;
  isPrimary: boolean;
  notes: string;
}

const EMPTY_FORM: ContactFormState = {
  firstName: "",
  lastName: "",
  title: "",
  email: "",
  phone: "",
  isPrimary: false,
  notes: "",
};

export function PartyContactsCard({
  type,
  partyId,
  contacts,
  canEdit,
}: {
  type: PartyType;
  partyId: string;
  contacts: PartyContactDto[];
  canEdit: boolean;
}) {
  const toast = useToast();
  const dialogs = useDialogs();
  const [form, setForm] = useState<ContactFormState | null>(null);

  const upsert = useUpsertPartyContact(type, partyId);
  const remove = useDeletePartyContact(type, partyId);

  const save = () => {
    if (!form) return;
    if (!form.firstName.trim() || !form.lastName.trim()) {
      toast.error("First and last name are required.");
      return;
    }
    const body: PartyContactRequest = {
      id: form.id ?? null,
      firstName: form.firstName,
      lastName: form.lastName,
      title: form.title || null,
      email: form.email || null,
      phone: form.phone || null,
      isPrimary: form.isPrimary,
      notes: form.notes || null,
    };
    upsert.mutate(body, {
      onSuccess: () => {
        toast.success(form.id ? "Contact updated." : "Contact added.");
        setForm(null);
      },
      onError: (e) => toast.error(e instanceof Error ? e.message : "Save failed."),
    });
  };

  const onDelete = async (c: PartyContactDto) => {
    const ok = await dialogs.confirm({
      title: `Remove ${c.firstName} ${c.lastName}?`,
      message: "The contact will no longer appear on this party.",
      confirmLabel: "Remove",
      destructive: true,
    });
    if (!ok) return;
    remove.mutate(c.id, {
      onSuccess: () => toast.success("Contact removed."),
      onError: (e) => toast.error(e instanceof Error ? e.message : "Remove failed."),
    });
  };

  return (
    <Card data-testid="party-contacts-card">
      <CardHeader className="flex items-center justify-between">
        <CardTitle>Contacts</CardTitle>
        {canEdit && !form && (
          <Button size="sm" variant="secondary" onClick={() => setForm(EMPTY_FORM)}>
            <Plus size={14} aria-hidden /> Add contact
          </Button>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-space-3">
        {contacts.length === 0 && !form && (
          <p className="text-caption text-gray-mid">No contacts on file.</p>
        )}

        {contacts.map((c) => (
          <div
            key={c.id}
            className="flex flex-wrap items-center gap-space-2 rounded border border-border-light p-space-3"
            data-testid={`party-contact-${c.id}`}
          >
            <div className="flex flex-col gap-space-1 grow">
              <div className="flex flex-wrap items-center gap-space-2">
                <span className="font-semibold text-onyx">
                  {c.firstName} {c.lastName}
                </span>
                {c.title && <span className="text-caption text-gray-mid">{c.title}</span>}
                {c.isPrimary && <StatusBadge tone="ok">Primary</StatusBadge>}
              </div>
              <span className="text-caption text-gray-mid">
                {[displayEmail(c.email), c.phone].filter(Boolean).join(" · ") || "No contact info"}
              </span>
              {c.notes && <span className="text-caption text-gray-mid">{c.notes}</span>}
            </div>
            {canEdit && (
              <div className="flex items-center gap-space-1">
                <Button
                  size="sm"
                  variant="secondary"
                  aria-label={`Edit ${c.firstName} ${c.lastName}`}
                  onClick={() =>
                    setForm({
                      id: c.id,
                      firstName: c.firstName,
                      lastName: c.lastName,
                      title: c.title ?? "",
                      email: c.email ?? "",
                      phone: c.phone ?? "",
                      isPrimary: c.isPrimary,
                      notes: c.notes ?? "",
                    })
                  }
                >
                  <Pencil size={14} aria-hidden />
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  aria-label={`Remove ${c.firstName} ${c.lastName}`}
                  onClick={() => void onDelete(c)}
                  disabled={remove.isPending}
                >
                  <Trash2 size={14} aria-hidden />
                </Button>
              </div>
            )}
          </div>
        ))}

        {form && (
          <div className="flex flex-col gap-space-2 rounded border border-border-light p-space-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-space-2">
              <div>
                <Label htmlFor="contact-first">First name</Label>
                <Input
                  id="contact-first"
                  value={form.firstName}
                  onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="contact-last">Last name</Label>
                <Input
                  id="contact-last"
                  value={form.lastName}
                  onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="contact-title">Title</Label>
                <Input
                  id="contact-title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="contact-email">Email</Label>
                <Input
                  id="contact-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="contact-phone">Phone</Label>
                <Input
                  id="contact-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="contact-notes">Notes</Label>
                <Input
                  id="contact-notes"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                />
              </div>
            </div>
            <label className="flex items-center gap-space-2 text-small text-onyx">
              <input
                type="checkbox"
                checked={form.isPrimary}
                onChange={(e) => setForm({ ...form, isPrimary: e.target.checked })}
              />
              Primary contact
            </label>
            <div className="flex items-center gap-space-2">
              <Button size="sm" onClick={save} loading={upsert.isPending}>
                {form.id ? "Save contact" : "Add contact"}
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setForm(null)}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
